import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  FolderOpen, FileText, Sparkles, Calendar, ArrowLeft, Plus,
  Loader2, Clock, Edit3, ChevronRight, User,
} from 'lucide-react'
import { projectsApi, opinionSessionsApi, Document, OpinionSessionListItem } from '../services/api'
import { useProject } from '../contexts/ProjectContext'

const DOC_TYPE_LABELS: Record<string, string> = {
  OpiniaPrawna: 'Opinia',
  Pozew: 'Pozew',
  Umowa: 'Umowa',
  PismoProcesowe: 'Pismo procesowe',
  Wezwanie: 'Wezwanie',
  Odwolanie: 'Odwołanie',
  Skarga: 'Skarga',
  Wniosek: 'Wniosek',
  Notatka: 'Notatka',
  OdpowiedzNaPozew: 'Odpowiedź na pozew',
}

type Tab = 'documents' | 'opinions'

function formatDate(dateString: string) {
  try {
    return new Date(dateString).toLocaleDateString('pl-PL')
  } catch {
    return dateString
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'Active':
      return { label: 'Aktywna', bg: 'bg-blue-100', text: 'text-blue-700' }
    case 'Completed':
      return { label: 'Zakonczona', bg: 'bg-green-100', text: 'text-green-700' }
    case 'Archived':
      return { label: 'Zarchiwizowana', bg: 'bg-gray-100', text: 'text-gray-700' }
    default:
      return { label: status, bg: 'bg-gray-100', text: 'text-gray-700' }
  }
}

function getDocTypeLabel(docType: string) {
  const labels: Record<string, string> = {
    legal_opinion: 'Opinia',
    response_to_claim: 'Odpowiedz na pozew',
    claim: 'Pozew',
    appeal: 'Apelacja',
    cassation: 'Skarga kasacyjna',
    memo: 'Notatka prawna',
  }
  return labels[docType] || docType
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { setCurrentProject } = useProject()
  const [activeTab, setActiveTab] = useState<Tab>('documents')

  // Fetch project details
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id!).then(r => r.data),
    enabled: !!id,
  })

  // Fetch documents for this project
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['project-documents', id],
    queryFn: () => projectsApi.getDocuments(id!, { pageSize: 100 }).then(r => r.data),
    enabled: !!id && activeTab === 'documents',
  })

  // Fetch opinion sessions for this project
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ['opinion-sessions', id],
    queryFn: () => opinionSessionsApi.list(id!).then(r => r.data),
    enabled: !!id && activeTab === 'opinions',
  })

  const documents = documentsData?.items || []

  const handleNavigateToGenerate = () => {
    if (project) {
      setCurrentProject(project)
    }
    navigate('/generate')
  }

  const handleResumeSession = (session: OpinionSessionListItem) => {
    if (project) {
      setCurrentProject(project)
    }
    if (session.status === 'Completed' && session.resultDocumentId) {
      navigate(`/documents/${session.resultDocumentId}`)
    } else {
      navigate(`/generate?sessionId=${session.id}`)
    }
  }

  const handleDocumentClick = (doc: Document) => {
    navigate(`/documents/${doc.id}`)
  }

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-16">
        <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">Nie znaleziono projektu</p>
        <Link to="/projects" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          Wróć do projektów
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Projekty
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <FolderOpen className="w-7 h-7 text-blue-600" />
              {project.name}
            </h1>
            {project.description && (
              <p className="text-gray-500 mt-1">{project.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
              {project.createdByName && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {project.createdByName}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(project.createdAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'documents'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Dokumenty
              {documentsData && (
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {documentsData.totalCount}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('opinions')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'opinions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Sesje opinii
              {sessions.length > 0 && (
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {sessions.length}
                </span>
              )}
            </span>
          </button>
        </nav>
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div>
          {documentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : documents.length === 0 ? (
            <div className="card p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="mb-2">Brak dokumentów w tym projekcie</p>
              <p className="text-sm">Dokumenty pojawia się po wygenerowaniu i zaakceptowaniu opinii</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(() => {
                const generated = documents.filter(d => d.isGenerated)
                const attached = documents.filter(d => !d.isGenerated)
                return (
                  <>
                    {generated.length > 0 && (
                      <>
                        <div className="px-1 pt-2 pb-1">
                          <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5" />
                            Wygenerowane dokumenty (AI)
                          </h3>
                        </div>
                        {generated.map((doc) => (
                          <div
                            key={doc.id}
                            onClick={() => handleDocumentClick(doc)}
                            className="card p-4 cursor-pointer hover:shadow-md transition-shadow flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <h3 className="font-medium text-gray-900 truncate">{doc.title}</h3>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">AI</span>
                                  <span className="bg-gray-100 px-2 py-0.5 rounded">{DOC_TYPE_LABELS[doc.type] ?? doc.type}</span>
                                  {doc.createdByName && <span>{doc.createdByName}</span>}
                                  <span>{formatDate(doc.updatedAt)}</span>
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                        ))}
                      </>
                    )}

                    {attached.length > 0 && (
                      <>
                        <div className={`px-1 pb-1 ${generated.length > 0 ? 'pt-4' : 'pt-2'}`}>
                          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" />
                            Załączone dokumenty
                          </h3>
                        </div>
                        {attached.map((doc) => (
                          <div
                            key={doc.id}
                            onClick={() => handleDocumentClick(doc)}
                            className="card p-4 cursor-pointer hover:shadow-md transition-shadow flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <h3 className="font-medium text-gray-900 truncate">{doc.title}</h3>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                  <span className="bg-gray-100 px-2 py-0.5 rounded">{DOC_TYPE_LABELS[doc.type] ?? doc.type}</span>
                                  {doc.createdByName && <span>{doc.createdByName}</span>}
                                  <span>{formatDate(doc.updatedAt)}</span>
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )
              })()}
            </div>
          )}
        </div>
      )}

      {/* Opinions Tab */}
      {activeTab === 'opinions' && (
        <div>
          {/* New opinion button */}
          <div className="mb-4">
            <button
              onClick={handleNavigateToGenerate}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nowa opinia
            </button>
          </div>

          {sessionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="card p-8 text-center text-gray-500">
              <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="mb-2">Brak sesji opinii</p>
              <p className="text-sm">Kliknij "Nowa opinia", aby rozpoczac generowanie</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sessions.map((session) => {
                const statusBadge = getStatusBadge(session.status)

                return (
                  <div
                    key={session.id}
                    onClick={() => handleResumeSession(session)}
                    className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-gray-900 line-clamp-2 flex-1 mr-2">
                        {session.title}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusBadge.bg} ${statusBadge.text}`}>
                        {statusBadge.label}
                      </span>
                    </div>

                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                      {session.query}
                    </p>

                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="flex items-center gap-3">
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {getDocTypeLabel(session.docType)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Edit3 className="w-3 h-3" />
                          v{session.currentVersion}
                        </span>
                      </div>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(session.updatedAt)}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
